import { Info, CheckCircle2, XCircle, Eye, Shield } from 'lucide-react';

export const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <Info size={24} />
            <h1 className="font-bold text-2xl">About VerifyPH</h1>
          </div>
          <a
            href="/"
            className="text-blue-100 hover:text-white text-sm underline"
          >
            ← Back to Home
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6 md:p-8 space-y-8">
          {/* Introduction */}
          <section className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0 mt-1">
                <Info className="text-blue-600" size={20} />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-gray-900 text-xl mb-3">What is VerifyPH?</h2>
                <p className="text-gray-700 leading-relaxed mb-2 font-semibold text-blue-600">
                  Transparency by the People, for the People
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  VerifyPH is a citizen-powered transparency platform designed to help the public document 
                  and monitor government-funded projects and public service delivery.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  VerifyPH enables citizens to submit evidence-based observations — such as photos, videos, 
                  and location data — related to public infrastructure and services. These submissions are 
                  reviewed through a moderation process and displayed transparently to support public awareness 
                  and informed discussion.
                </p>
              </div>
            </div>
          </section>

          {/* What VerifyPH Does */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="text-green-600" size={24} />
              <h2 className="font-bold text-gray-900 text-xl">What VerifyPH Does</h2>
            </div>
            <ul className="space-y-3 ml-2">
              <li className="flex items-start gap-3">
                <div className="bg-green-50 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                  <CheckCircle2 className="text-green-600" size={16} />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">Collects citizen-submitted reports</strong> supported by evidence
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-green-50 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                  <CheckCircle2 className="text-green-600" size={16} />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">Displays verified public submissions</strong> through maps and dashboards
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-green-50 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                  <CheckCircle2 className="text-green-600" size={16} />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">Aggregates reports</strong> to show trends and timelines
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-green-50 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                  <CheckCircle2 className="text-green-600" size={16} />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">Provides open, accessible transparency tools</strong> for citizens, media, and civil society
                </p>
              </li>
            </ul>
          </section>

          {/* What VerifyPH Does NOT Do */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <XCircle className="text-red-600" size={24} />
              <h2 className="font-bold text-gray-900 text-xl">What VerifyPH Does NOT Do</h2>
            </div>
            <ul className="space-y-3 ml-2">
              <li className="flex items-start gap-3">
                <div className="bg-red-50 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                  <XCircle className="text-red-600" size={16} />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Make legal determinations or accusations
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-red-50 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                  <XCircle className="text-red-600" size={16} />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Declare projects compliant or non-compliant
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-red-50 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                  <XCircle className="text-red-600" size={16} />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Replace government oversight bodies
                </p>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-red-50 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                  <XCircle className="text-red-600" size={16} />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Endorse political parties, candidates, or individuals
                </p>
              </li>
            </ul>
          </section>

          {/* Anti-Epal Watch Section */}
          <section className="bg-blue-50 border border-blue-200 rounded-lg p-5 md:p-6 space-y-3">
            <div className="flex items-center gap-2">
              <Eye className="text-blue-600" size={20} />
              <h2 className="font-bold text-gray-900 text-lg">Anti-Epal Watch</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Anti-Epal Watch is a reporting category within VerifyPH that documents observed promotional 
              materials on government-funded projects. These reports are presented for transparency and public 
              reference only.
            </p>
          </section>

          {/* Our Principle */}
          <section className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5 md:p-6 space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="text-blue-600" size={20} />
              <h2 className="font-bold text-gray-900 text-lg">Our Principle</h2>
            </div>
            <div className="space-y-2">
              <p className="text-gray-700 leading-relaxed font-medium italic">
                VerifyPH documents citizen-submitted evidence. It does not make legal judgments, accusations, or official determinations.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Transparency is strongest when evidence is visible and process is clear. VerifyPH exists to document what citizens observe — not to judge, accuse, or prosecute.
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};
